// Global analytics dashboard — /admin/analytics. Sums every tenant's daily
// rollup rows for the selected date range and surfaces a top-10 leaderboard
// + a "quiet tenants" callout that follow the chart metric. The "are tenants
// actually using this?" view for the superadmin.
//
// The stat cards double as the trend chart's metric selector (click a card
// to plot it) — same interaction as the owner Analytics page. Active
// customers stays a plain card: it's a snapshot, not a leaderboard-sortable
// counter, and the API rejects it as a leaderboard metric.

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { AdminApiError } from '../adminApi.js'
import {
  useGlobalAnalytics,
  type LeaderboardMetric,
} from '../hooks/useAnalytics.js'
import { AdminLayout } from '../components/AdminLayout.js'
import {
  AnalyticsRangeBar,
  defaultRange,
  rangeSpanDays,
  type RangePreset,
} from '../components/AnalyticsRangeBar.js'
import { AnalyticsStatCard } from '../components/AnalyticsStatCard.js'
import { TrendChart } from '../../shared/trend-chart/TrendChart.js'

const METRIC_CHART_OPTIONS: Array<{
  key: LeaderboardMetric
  label: string
  unit: string
}> = [
  { key: 'passes_installed', label: 'Passes installed', unit: 'passes' },
  { key: 'stamps_issued', label: 'Stamps issued', unit: 'stamps' },
  { key: 'rewards_redeemed', label: 'Rewards redeemed', unit: 'rewards' },
  { key: 'cycle_completions', label: 'Cycle completions', unit: 'cycles' },
  { key: 'broadcasts_sent', label: 'Broadcasts sent', unit: 'broadcasts' },
]

export function AdminAnalytics() {
  const [range, setRange] = useState(defaultRange())
  const [chartMetric, setChartMetric] =
    useState<LeaderboardMetric>('stamps_issued')

  const q = useGlobalAnalytics(range.from, range.to, chartMetric)

  function handleRangeChange(from: string, to: string, preset: RangePreset) {
    setRange({ from, to, preset })
  }

  const activeMetric = METRIC_CHART_OPTIONS.find((m) => m.key === chartMetric)!
  const chartData =
    q.data?.daily.map((d) => ({ date: d.date, value: d[chartMetric] })) ?? []
  const spanDays = rangeSpanDays(range.from, range.to)

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <div>
          <h1>Analytics</h1>
          <p className="admin-audit-meta" style={{ marginTop: 6 }}>
            Platform-wide usage across every tenant. Updates nightly after
            8pm PH; use “Refresh now” for an on-demand recompute. Click a
            card to chart that metric.
          </p>
        </div>
      </div>

      <div className="admin-section">
        <AnalyticsRangeBar
          from={range.from}
          to={range.to}
          preset={range.preset}
          onChange={handleRangeChange}
          lastRefreshedAt={q.data?.last_refreshed_at}
          loading={q.isFetching}
        />

        {q.isLoading && (
          <p className="admin-audit-meta">Loading analytics…</p>
        )}
        {q.isError && (
          <p className="admin-audit-meta">
            {q.error instanceof AdminApiError
              ? q.error.userMessage
              : "Couldn't load analytics."}
          </p>
        )}
        {q.data && (
          <>
            <div className="admin-stat-grid">
              {METRIC_CHART_OPTIONS.map((m) => (
                <AnalyticsStatCard
                  key={m.key}
                  label={m.label}
                  value={q.data.totals[m.key]}
                  prev={q.data.previous_totals[m.key]}
                  prevSpanDays={spanDays}
                  selected={chartMetric === m.key}
                  onSelect={() => setChartMetric(m.key)}
                />
              ))}
              <AnalyticsStatCard
                label="Active customers"
                value={q.data.totals.active_customers}
                sub="last 30 days, summed across tenants"
              />
            </div>

            <div className="admin-chart" style={{ marginTop: 18 }}>
              <div className="admin-chart-title">{activeMetric.label}</div>
              <TrendChart data={chartData} unit={activeMetric.unit} />
              {!chartData.some((d) => d.value > 0) && (
                <p className="admin-chart-empty">
                  No {activeMetric.unit} recorded in this range yet.
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <div className="admin-section" style={{ marginTop: 24 }}>
        <h3>Top tenants by {activeMetric.label.toLowerCase()}</h3>
        <div className="admin-card" style={{ padding: 0 }}>
          {q.data?.leaderboard.length ? (
            q.data.leaderboard.map((row, idx) => (
              <div key={row.tenant_id} className="admin-leaderboard-row">
                <span className="admin-leaderboard-rank">{idx + 1}</span>
                <Link
                  to={`/admin/tenants/${row.tenant_id}?tab=analytics`}
                  className="admin-leaderboard-name"
                >
                  {row.tenant_name}
                </Link>
                <span className="admin-leaderboard-value">
                  {row.value.toLocaleString()} {activeMetric.unit}
                </span>
              </div>
            ))
          ) : (
            <div style={{ padding: 18 }} className="admin-audit-meta">
              No tenant activity in this range yet.
            </div>
          )}
        </div>
      </div>

      {q.data && q.data.quiet_tenants.length > 0 && (
        <div className="admin-section admin-section-sub" style={{ marginTop: 24 }}>
          <h3>Quiet tenants (no stamps in range)</h3>
          <p className="admin-audit-meta">
            Active tenants with zero stamp activity for the selected window.
          </p>
          <div className="admin-card" style={{ padding: 0 }}>
            {q.data.quiet_tenants.map((row) => (
              <div key={row.tenant_id} className="admin-leaderboard-row">
                <span className="admin-leaderboard-rank">·</span>
                <Link
                  to={`/admin/tenants/${row.tenant_id}?tab=analytics`}
                  className="admin-leaderboard-name"
                >
                  {row.tenant_name}
                </Link>
                <span className="admin-leaderboard-value">0 stamps</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
